import { useState, type FormEvent } from 'react';
import { brand } from '@/config/brand';
import { feedbackConfig } from '@/config/feedback';

const TYPES = [
  { value: 'add-drink', label: 'Add a drink / recipe' },
  { value: 'fix-data', label: 'Fix ABV, calories, or price' },
  { value: 'feature', label: 'Feature idea' },
  { value: 'bug', label: 'Bug report' },
  { value: 'other', label: 'Other' },
] as const;

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function FeedbackPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<(typeof TYPES)[number]['value']>('add-drink');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = message.trim();
    if (trimmed.length < 8) {
      setError('Please include a bit more detail (at least a sentence).');
      return;
    }

    const payload = {
      name: name.trim() || null,
      email: email.trim() || null,
      type,
      message: trimmed,
      page: typeof window !== 'undefined' ? window.location.href : '/feedback/',
      site: brand.domain,
      sentAt: new Date().toISOString(),
    };

    if (feedbackConfig.endpoint) {
      setStatus('sending');
      try {
        const res = await fetch(feedbackConfig.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        setStatus('sent');
        setMessage('');
      } catch {
        setStatus('error');
        setError(
          'Could not send right now. You can email us instead — try the mailto link below.',
        );
      }
      return;
    }

    const subject = encodeURIComponent(
      `${feedbackConfig.subjectPrefix} ${TYPES.find((t) => t.value === type)?.label ?? type}`,
    );
    const body = encodeURIComponent(
      [
        `Type: ${type}`,
        name.trim() ? `Name: ${name.trim()}` : null,
        email.trim() ? `Reply-to: ${email.trim()}` : null,
        `Page: ${payload.page}`,
        '',
        trimmed,
      ]
        .filter(Boolean)
        .join('\n'),
    );
    window.location.href = `mailto:${feedbackConfig.mailto}?subject=${subject}&body=${body}`;
    setStatus('sent');
  }

  return (
    <article className="panel prose-page">
      <h1>Feedback</h1>
      <p>
        Request a drink, flag bad data, or suggest an improvement. We use this to
        grow the {brand.name} catalog.
      </p>

      {status === 'sent' && (
        <p role="status" className="ok-banner">
          Thanks — your feedback is ready to send
          {feedbackConfig.endpoint ? '.' : ' via your email app.'}
        </p>
      )}

      <form className="feedback-form" onSubmit={onSubmit}>
        <label>
          What is this about?
          <select
            className="select"
            value={type}
            onChange={(e) =>
              setType(e.target.value as (typeof TYPES)[number]['value'])
            }
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          Your message
          <textarea
            className="input feedback-form__textarea"
            required
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Please add Kentucky Mule with 2 oz bourbon…"
          />
        </label>

        <label>
          Name <span className="muted">(optional)</span>
          <input
            className="input"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label>
          Email <span className="muted">(optional, if you want a reply)</span>
          <input
            className="input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={status === 'sending'}
        >
          {status === 'sending' ? 'Sending…' : 'Submit feedback'}
        </button>
      </form>

      <p className="muted" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
        {feedbackConfig.endpoint
          ? 'Messages go to our feedback inbox.'
          : `AWS email wiring is pending publish — for now this opens mail to ${feedbackConfig.mailto}.`}
      </p>
    </article>
  );
}
